/**
 * A culture note's Georgian terms (M6b step 8).
 *
 * A culture note is English prose ABOUT Georgian — "A სუფრა is a Georgian feast, and the person
 * who runs it is the თამადა" — and the one interactive thing in it is those terms: the reader
 * makes each tappable so the learner can look it up, which is why the prompt insists they be
 * written in Georgian script rather than transliterated.
 *
 * It is NOT rendered by the reading surface, and that is deliberate. `Transcript` tokenizes on
 * whitespace, draws marks from `line_item`, and offers every token as a tap target — all of it
 * built for Georgian. Run over English prose it would produce marks on English words, gloss
 * requests for "feast", and a wall of tap targets over text that is already in the reader's own
 * language. So the note gets this instead: split the prose into runs, and only the Georgian ones
 * are interactive.
 */

/**
 * A run of Georgian script, plus whatever punctuation and hyphenation sits inside it.
 *
 * The block is U+10A0–U+10FF (Mkhedruli, Asomtavruli, Nuskhuri) plus U+1C90–U+1CBF (Mtavruli) —
 * the same range the tokenizer and the authoring validator use. Trailing punctuation is left OUT
 * of the run: "თამადა." would otherwise be looked up with the full stop attached, and the gloss
 * route keys on the surface.
 */
const GEORGIAN_RUN = /[Ⴀ-ჿᲐ-Ჿ]+(?:[-‐'’][Ⴀ-ჿᲐ-Ჿ]+)*/gu;

export interface CultureRun {
  text: string;
  /** true = Georgian script, and therefore tappable */
  term: boolean;
}

/**
 * One paragraph → alternating English and Georgian runs.
 *
 * Every character survives: the runs concatenate back to the input exactly. That is the property
 * worth holding, because the alternative to getting it right is prose with words silently missing
 * from it, which no test of the tappable half would ever notice.
 */
export function cultureRuns(paragraph: string): CultureRun[] {
  const out: CultureRun[] = [];
  let at = 0;
  for (const m of paragraph.matchAll(GEORGIAN_RUN)) {
    const start = m.index;
    if (start > at) out.push({ text: paragraph.slice(at, start), term: false });
    out.push({ text: m[0], term: true });
    at = start + m[0].length;
  }
  if (at < paragraph.length) out.push({ text: paragraph.slice(at), term: false });
  return out;
}

/** The distinct Georgian terms in a note, in the order they appear — for the "N terms" line. */
export function cultureTerms(paragraphs: readonly string[]): string[] {
  const seen: string[] = [];
  for (const p of paragraphs) {
    for (const run of cultureRuns(p)) {
      if (run.term && !seen.includes(run.text)) seen.push(run.text);
    }
  }
  return seen;
}
